import { useCallback, useEffect, useState } from 'react'
import toast from 'react-hot-toast'
import { AlertTriangle, Check } from 'lucide-react'
import cd2bPropuestaService from '@/services/cd2bPropuestaService'
import { mensajeDeError } from '@/lib/errores'

/**
 * CÓMO SE ENTREGA LA EMPRESA: con los datos del asesor, sin los de ejemplo, o vacía.
 *
 * Antes eran dos botones —«Limpiar» y «Conservar»— y «limpiar» revertía TODA carga viva, también
 * las del sistema anterior. El asesor que importaba el catálogo y la cartera del cliente y
 * elegía entregar limpio le borraba justo los datos reales. Aquí cada salida dice qué se va y
 * qué se queda, con lo que hay de cada cosa delante.
 *
 * Lo comparten el editor de la cotización y la ficha del cliente: la decisión es una sola y se
 * guarda en la cotización, así que las dos pantallas no pueden decir cosas distintas.
 */
const OPCIONES = [
  {
    clave: 'conservar',
    titulo: 'Con los datos del asesor',
    detalle: 'Se queda todo: lo importado del sistema anterior, lo tecleado y los datos de ejemplo.',
  },
  {
    clave: 'quitarEjemplo',
    titulo: 'Sin los datos de ejemplo',
    detalle: 'Se quitan solo las siembras de ejemplo. Lo importado y lo tecleado se queda.',
    necesitaEjemplo: true,
  },
  {
    clave: 'vaciar',
    titulo: 'Vacía',
    detalle:
      'Se va toda la operación: ejemplo, importado y tecleado. Se conservan la configuración, el emisor, los usuarios y el plan de cuentas.',
  },
]

export default function DatosAlEntregar({ propuestaId, onCambio }) {
  const [estado, setEstado] = useState(null)
  const [guardando, setGuardando] = useState(null)

  const leer = useCallback(async () => {
    if (!propuestaId) return
    try {
      const r = await cd2bPropuestaService.listarDatosPrueba(propuestaId)
      setEstado(r?.data || null)
    } catch {
      // Sin canal con la empresa no hay cifras que enseñar; el chequeo de entrega lo dice aparte.
      setEstado(null)
    }
  }, [propuestaId])

  useEffect(() => {
    leer()
  }, [leer])

  const decidir = async (clave) => {
    setGuardando(clave)
    try {
      await cd2bPropuestaService.decidirDatosPrueba(propuestaId, clave)
      toast.success('Guardado: así se entregará la empresa.')
      await leer()
      onCambio?.()
    } catch (e) {
      toast.error(mensajeDeError(e, 'No se pudo guardar la decisión'))
    } finally {
      setGuardando(null)
    }
  }

  if (!estado) return null
  const filasEjemplo = Number(estado.filasEjemplo) || 0
  const filasImportadas = Number(estado.filasImportadas) || 0
  const decision = estado.decision || null
  const falta = !decision && (estado.activas || 0) > 0

  return (
    <div className="space-y-2">
      <div>
        <p className="text-body-sm font-semibold text-neutral-900 dark:text-neutral-50">Cómo se entrega</p>
        <p className="text-caption tabular-nums text-neutral-500 dark:text-neutral-400">
          Importado del sistema anterior: {filasImportadas.toLocaleString('es-SV')} fila(s) · De ejemplo:{' '}
          {filasEjemplo.toLocaleString('es-SV')} fila(s)
        </p>
      </div>
      {falta && (
        <p className="flex items-start gap-1.5 text-body-sm text-warning-700 dark:text-warning-300">
          <AlertTriangle size={14} className="mt-0.5 shrink-0" />
          Elige una opción: sin ella no se puede pasar a producción.
        </p>
      )}
      <div className="grid gap-2 sm:grid-cols-3" role="radiogroup" aria-label="Cómo se entrega la empresa">
        {OPCIONES.map((o) => {
          const elegida = decision === o.clave
          const inutil = o.necesitaEjemplo && filasEjemplo === 0 && !elegida
          return (
            <button
              key={o.clave}
              type="button"
              role="radio"
              aria-checked={elegida}
              disabled={inutil || guardando !== null}
              onClick={() => decidir(o.clave)}
              title={inutil ? 'No hay datos de ejemplo que quitar.' : undefined}
              className={`flex flex-col gap-1 rounded-md border px-3 py-2 text-left transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 disabled:cursor-not-allowed disabled:opacity-50 ${
                elegida
                  ? 'border-primary bg-primary-50 dark:border-primary-400 dark:bg-primary/15'
                  : 'border-neutral-200 hover:border-neutral-300 dark:border-neutral-800 dark:hover:border-neutral-700'
              }`}
            >
              <span className="flex items-center gap-1.5 text-body-sm font-medium text-neutral-900 dark:text-neutral-50">
                {elegida && <Check size={14} className="shrink-0 text-primary-600 dark:text-primary-300" />}
                {guardando === o.clave ? 'Guardando…' : o.titulo}
              </span>
              <span className="text-caption text-neutral-600 dark:text-neutral-400">{o.detalle}</span>
            </button>
          )
        })}
      </div>
    </div>
  )
}
